'use client';

import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { ajvResolver } from '@hookform/resolvers/ajv';
import { Button, Input, Image } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { AnimationTransition } from '@/components/animation/AnimationTransition';
import { AnimationBackground } from '@/components/animation/AnimationBackground';
import { schemaLogin } from '../schemas';
import { useLoginCRUD } from '../hook/useLoginCRUD';
import { inputClasses } from '../constant';
import { AnimationInitial } from '@/components/animation/AnimationInitial';
import { AnimationGestures } from '@/components/animation/AnimationGestures';
import { ILoginVariables } from '@/domain/auth/entities';
import { validateEmail } from '@/utils/validate/email';
import { useBusinessStore } from '@/stores/businessStore';
import { useProfileStore } from '@/stores/profileStore';
import { ASSETS } from '@/constants/assets';
import { Link } from '@/components/page-loader/link';
import { useLocalStorage } from 'react-use';
import { STORAGE_KEY } from '@/constants/keys';
import { useBrandStore } from '@/stores/brandStore';
// import { AnimationHover } from '@/components/animation/AnimationHover';

interface LoginProps {}

const Login: React.FC<LoginProps> = () => {
  const { brand } = useBrandStore();
  const { onLogin, isVisible, toggleVisibility, isDisabled } = useLoginCRUD();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<ILoginVariables>({
    resolver: ajvResolver(schemaLogin),
  });

  const { setListBusiness, setSelectBusiness } = useBusinessStore();
  const { setValAuth, setValProfile } = useProfileStore();
  const [_, setEmailNotYetVerify] = useLocalStorage<string>(STORAGE_KEY.EMAIL_NOT_VERIFY);

  useEffect(() => {
    setListBusiness([]);
    setSelectBusiness(undefined);
    setValAuth(undefined);
    setValProfile(undefined);
    setEmailNotYetVerify('');
  }, []);

  const onSubmitLogin = handleSubmit(valForm => {
    const { email } = valForm;
    if (!validateEmail(email)) {
      setError('email', {
        type: 'manual',
        message: 'Email is invalid',
      });
      return;
    }

    onLogin(valForm);
  });

  return (
    <AnimationInitial>
      <div className="absolute w-screen h-screen bg-gradient-to-br from-rose-400 via-fuchsia-500 to-indigo-500">
        <AnimationBackground />
      </div>
      <AnimationTransition>
        <div className="relative">
          <div className="flex flex-col items-center justify-center w-screen h-screen p-2 sm:p-4 lg:p-8">
            <div className="mb-[40px]">
              <Image
                src={brand?.logo ?? ASSETS.CRUNCHER_LOGO_WHITE}
                className="object-contain"
                alt="Cruncher Logo"
                width={280}
                height={80}
              />
            </div>
            <div className="flex flex-col items-center w-full">
              <div className="w-full max-w-sm">
                {/* <AnimationHover> */}
                <div className="flex flex-col w-full gap-4 px-8 pt-6 pb-10 rounded-large bg-background/60 shadow-small backdrop-blur-md backdrop-saturate-150 dark:bg-default-100/50">
                  <p className="pb-2 text-xl font-medium">Log In</p>
                  <form className="flex flex-col gap-3" onSubmit={onSubmitLogin} noValidate>
                    <Input
                      classNames={inputClasses}
                      label="Email Address"
                      placeholder="Enter your email"
                      type="text"
                      variant="bordered"
                      isRequired
                      isInvalid={errors.email ? true : false}
                      errorMessage={errors.email && errors.email.message ? errors.email.message : 'Please enter a valid email'}
                      {...register('email')}
                    />
                    <Input
                      classNames={inputClasses}
                      endContent={
                        <button type="button" onClick={toggleVisibility}>
                          {isVisible ? (
                            <Icon className="text-2xl pointer-events-none text-foreground/50" icon="solar:eye-bold" />
                          ) : (
                            <Icon className="text-2xl pointer-events-none text-foreground/50" icon="solar:eye-closed-linear" />
                          )}
                        </button>
                      }
                      label="Password"
                      placeholder="Enter your password"
                      type={isVisible ? 'text' : 'password'}
                      variant="bordered"
                      isRequired
                      isInvalid={errors.password ? true : false}
                      errorMessage={errors.password && errors.password.message ? errors.password.message : 'Please enter a valid email'}
                      {...register('password')}
                    />
                    <div className="flex items-center justify-end px-1 py-2">
                      {/* <Checkbox
                        classNames={{
                          wrapper: 'before:border-foreground/50',
                        }}
                        name="remember"
                        size="sm"
                      >
                        Remember me
                      </Checkbox> */}
                      <Link href="/forgot-password" className="text-sm text-foreground/50">
                        Forgot password?
                      </Link>
                    </div>
                    <AnimationGestures>
                      <Button isLoading={isDisabled} className="w-full bg-foreground/10 dark:bg-foreground/20" type="submit">
                        Log In
                      </Button>
                    </AnimationGestures>
                  </form>
                  {/* <div className="flex items-center gap-4 py-2">
                    <Divider className="flex-1" />
                    <p className="shrink-0 text-tiny text-default-500">OR</p>
                    <Divider className="flex-1" />
                  </div> */}
                  <p className="text-center text-small text-foreground/50">
                    Need to create an account?&nbsp;
                    <Link className="text-foreground" href="/signup">
                      Sign Up
                    </Link>
                  </p>
                  <p className="hidden text-right text-tiny text-foreground/50">2024-11-12_21:09</p>
                </div>
                {/* </AnimationHover> */}
              </div>
            </div>
          </div>
        </div>
      </AnimationTransition>
    </AnimationInitial>
  );
};

export default Login;
